import {ParentContext} from 'app/hooks/ApiParentContext'
import {DateTime} from 'luxon'
import {
  JsonObject,
  JsonObjectWithId,
  cloneJson,
  jsonDataIsArrayWithIds,
  jsonDataIsObjWithId,
  mergeJsonObject,
} from 'utilities/types'

export interface IBaseRecord {
  id?: string
  created?: {
    by?: {id?: string}
    at?: string
  }
  updated?: {
    by?: {id?: string}
    at?: string
  }
}

export type ApiListItem = JsonObjectWithId & {
  name: string | undefined
}

export type ChildPath = {
  parentClass: CRecordClass
  childClass: CRecordClass
  prop: string
  reverse: string | null
}

export type DescendantChain = {
  subprop: string
  parentClass: CRecordClass
  childClass: CRecordClass
}[]

export class CBaseRecord<IType extends IBaseRecord> {
  id
  created
  updated

  static external_id: string
  static record_name: string
  static children?: Record<string, CRecordClass>

  _data
  static _registered = false
  static _parents?: [CRecordClass, string][]
  static isRoot = false

  static hasChild(prop: string) {
    return !!(this.children && prop in this.children)
  }

  static get isTopLevel() {
    if (!this._registered) {
      throw Error(
        `Cannot return ${this.name}.isTopLevel because ${this.name} is not listed as the child of any other record class. If it is a top-level record type, add it to the children of CRoot.`
      )
    }
    for (const parent of this.parentRelationships) {
      if (parent.parentClass.isRoot) {
        return true
      }
    }
    return false
  }

  static get maxDepth() {
    let output = 0
    for (const path of this.childPaths) {
      const subdepth = path.childClass.maxDepth + 1
      if (subdepth > output) {
        output = subdepth
      }
    }
    return output
  }
  static getDescendantChain(prop: string): DescendantChain {
    const childProps = Object.keys(this.children ?? {})
    // sort props from longest to shortest
    childProps.sort((x, y) => y.length - x.length)

    for (const subprop of childProps) {
      const path = this.childPath(subprop)
      if (subprop === prop) {
        return [{subprop: prop, parentClass: this, childClass: path.childClass}]
      }
      if (subprop.length >= prop.length + 1) {
        continue
      }
      const match = prop.slice(0, subprop.length)
      const dot = prop.slice(subprop.length, subprop.length + 1)
      const remainder = prop.slice(subprop.length + 1)
      if (match === subprop && dot === '.') {
        return [
          {subprop: subprop, parentClass: this, childClass: path.childClass},
          ...path.childClass.getDescendantChain(remainder),
        ]
      }
    }
    throw new Error(`${this.name} has no descendant chain matching "${prop}".`)
  }

  static parentContexts(data: JsonObject) {
    const output = []
    for (const rel of this.parentRelationships) {
      if (rel.reverse && rel.reverse in data) {
        const parentId = data[rel.reverse]
        if (typeof parentId === 'string') {
          output.push(
            new ParentContext({
              parentClass: rel.parentClass,
              childClass: rel.childClass,
              parentId,
              prop: rel.prop,
              childId: typeof data.id === 'string' ? data.id : undefined,
            })
          )
        }
      } else if (rel.parentClass.isRoot) {
        output.push(
          new ParentContext({
            parentClass: rel.parentClass,
            childClass: rel.childClass,
            parentId: 'root',
            prop: rel.prop,
            childId: typeof data.id === 'string' ? data.id : undefined,
          })
        )
      }
    }
    return output
  }

  static guessParentContext(data: JsonObject, errorOnFail?: boolean) {
    const output = this.parentContexts(data)
    if (!output.length) {
      // the default of errorOnFail is true; if it's undefined, treat it as true
      if (errorOnFail !== false) {
        throw new Error(`Cannot extract a parent context from data: ${JSON.stringify(data)}`)
      }
      return undefined
    }
    return output[0]
  }

  static childPath(prop: string): ChildPath {
    if (!this.children || !(prop in this.children)) {
      throw Error(`${this.name}.childPath("${prop}") could not find that path.`)
    }
    const child = this.children[prop]
    return {
      parentClass: this,
      childClass: child,
      prop,
      reverse: this.external_id ?? null,
    }
  }

  static get childPaths(): ChildPath[] {
    if (!this.children) {
      return []
    }
    const output = []
    for (const prop in this.children) {
      output.push(this.childPath(prop))
    }
    return output
  }

  static childClass(prop: string): CRecordClass {
    const childPath = this.childPath(prop)
    return childPath.childClass
  }

  static registerWithDescendants() {
    if (this._registered) {
      return
    }
    this._registered = true
    for (const prop in this.children) {
      const childPath = this.childPath(prop)
      childPath.childClass._addParent(this, prop)
      childPath.childClass.registerWithDescendants()
    }
  }

  static _addParent(recordClass: CRecordClass, prop: string) {
    if (!this._parents) {
      this._parents = []
    }
    for (const elem of this._parents) {
      if (elem[0] === recordClass && elem[1] === prop) {
        return
      }
    }
    this._parents.push([recordClass, prop])
  }

  static get parentRelationships(): ChildPath[] {
    if (!this._registered) {
      throw Error(
        `Cannot return ${this.name}.parentRelationships because ${this.name} is not listed as the child of any other record class. If it is a top-level record type, add it to the children of CRoot.`
      )
    }
    return (
      this._parents?.map((parent) => {
        const [parentClass, prop] = parent
        return parentClass.childPath(prop)
      }) ?? []
    )
  }

  constructor(data: JsonObject & IType) {
    this.id = data.id
    const timestamp = DateTime.now().toISO() ?? undefined
    this.created = data.created ?? {at: timestamp, by: {id: undefined}}
    this.updated = data.updated ?? {at: timestamp, by: {id: undefined}}
    this._data = data
  }

  export() {
    return this._data
  }

  static toListItem(data: JsonObjectWithId): ApiListItem {
    const output = {id: data.id, name: data.name ?? undefined} as ApiListItem
    for (const prop in data) {
      if (prop === 'name' || prop.substring(0, 2) === '__') {
        output[prop] = data[prop]
      }
    }
    return output
  }

  static mergeChild(
    data: JsonObjectWithId,
    prop: string,
    mode: 'add' | 'remove',
    childData: JsonObjectWithId
  ) {
    const propFix = prop != 'people' ? prop : 'persons'
    if (propFix in data) {
      const list = data[propFix]
      if (jsonDataIsArrayWithIds(list)) {
        let found = false
        let newList: JsonObjectWithId[]
        if (mode === 'add') {
          newList = list.map((item) => {
            if (item.id === childData.id) {
              found = true
              const output: JsonObjectWithId = {id: item.id}
              for (const prop in item) {
                output[prop] = item[prop]
              }
              for (const prop in childData) {
                if (prop.substring(0, 2) === '__') {
                  output[prop] = childData[prop]
                }
              }
              return output
            }
            return item
          })
          if (!found) {
            newList.push(childData)
          }
        } else {
          newList = list.filter((item) => item.id !== childData.id)
        }
        return this.merge(data, {[prop]: newList})
      }
    }
  }

  static merge(
    data: JsonObjectWithId | undefined,
    updates: JsonObject | undefined
  ): JsonObjectWithId {
    if (data === undefined) {
      if (!jsonDataIsObjWithId(updates)) {
        throw new Error(`Cannot merge() undefined with an incomplete update.`)
      }
      return cloneJson(updates)
    }
    if (updates === undefined) {
      return cloneJson(data)
    }
    return mergeJsonObject(data, updates)
  }
}

export type CRecordClass = typeof CBaseRecord<any>
